import React from "react";
import PropTypes from "prop-types";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import { CardListItem } from "src/app/components/ContentElements/CardListItem/CardListItem";
import iconsMap from "src/assets/iconsMap";
import SearchBar from "src/app/components/SearchBar/SearchBar";
import SearchHeader from "src/app/components/SearchHeader/SearchHeader";
import ProgressBar from "src/app/components/ProgressBar/ProgressBar";
import FixedErrorBox from "src/app/components/FixedErrorBox/FixedErrorBox";
import Button from "src/app/components/ContentElements/Button/Button";
import OrderSearchMessage from "src/app/components/OrderSearchMessage";
import useSearchProvider from "src/app/hooks/useSearchProvider";
import styles from "./SearchList.module.css";

const SearchList = (state) => {
  const {
    searches,
    currentPage,
    prevButtonIsActive,
    nextButtonIsActive,
    onPreviousButtonClick,
    onNextButtonClick,
    itemToFocus,
    loading,
    fixedErrorVisible,
    failedDirection,
  } = useSearchProvider(state);

  const {
    baseUrl = "",
    searchListCopy: {
      loadPrevious = "",
      loadMore = "",
      faqsFound = "",
      viewedText = "",
      clientErrorContent,
    } = {},
    searchBox: { yourSearchResultsFor = {}, searchAgain = {} } = {},
    query,
    isOrderSearch = false,
    itemCount: searchesTotal = "0",
    pageSize,
  } = state;

  const icon = iconsMap("Related Article");

  const nextButton = {
    text: loadMore,
    link: {
      href: `?query=${query}&page=${currentPage + 2}`,
      target: null,
    },
    dataPage: currentPage + 1,
    target: null,
  };

  const prevButton = {
    text: loadPrevious,
    link: {
      href:
        currentPage < 1
          ? `?query=${query}`
          : `?query=${query}&page=${currentPage}`,
      target: null,
    },
    dataPage: currentPage + 1,
    target: null,
  };

  return (
    <>
      <SearchHeader
        title={yourSearchResultsFor["yourSearchResultsFor-text"]}
        query={query}
      />
      <div className={styles.searchContainer}>
        <SearchBar
          placeholder={searchAgain["searchAgainHint-text"]}
          displayDropdown={true}
          type="search-page"
        />
      </div>
      <h2 className={styles.foundFaqs} role="presentation">
        {faqsFound.replace("<x>", searchesTotal)}
      </h2>
      {isOrderSearch && <OrderSearchMessage />}
      {prevButtonIsActive && (
        <div className={styles.buttonContainer}>
          <Button
            data={prevButton}
            role="button"
            onClick={onPreviousButtonClick}
            loading={loading}
          />
        </div>
      )}
      <ul>
        <TransitionGroup component={null}>
          {searches.length > 0 &&
            searches.map((item, i) => {
              const link = baseUrl ? `${baseUrl}${item.href}` : item.href; // we need to do this to append for instance /ru/ in front of the faq urls
              return (
                <CSSTransition
                  key={`${item.text}-${i}`}
                  timeout={500}
                  classNames="global_item"
                >
                  <CardListItem
                    key={item.title}
                    link={link}
                    text={item.title}
                    iconPosition="right"
                    icon={icon}
                    ariaPosInSet={i + 1}
                    ariaSetSize={searches.length}
                    isFocused={itemToFocus === i}
                  />
                </CSSTransition>
              );
            })}
        </TransitionGroup>
      </ul>
      <ProgressBar
        viewedText={viewedText}
        totalItems={searchesTotal}
        totalVisibleItems={
          pageSize * (currentPage + 1) < searchesTotal
            ? pageSize * (currentPage + 1)
            : searchesTotal
        }
      />
      {nextButtonIsActive && (
        <div className={styles.buttonContainer}>
          <Button
            data={nextButton}
            role="button"
            onClick={onNextButtonClick}
            loading={loading}
          />
        </div>
      )}
      <FixedErrorBox
        visible={fixedErrorVisible}
        header={clientErrorContent[0]?.clientSearchErrorTitle}
        description={clientErrorContent[0]?.clientSearchErrorDescription}
        retryText={clientErrorContent[0]?.retryText}
        onClick={
          failedDirection === "prev" ? onPreviousButtonClick : onNextButtonClick
        }
      />
    </>
  );
};

export default SearchList;

SearchList.propTypes = {
  searchListCopy: PropTypes.object.isRequired,
  header: PropTypes.object,
  items: PropTypes.array.isRequired,
  query: PropTypes.string.isRequired,
  searchBox: PropTypes.object,
  placeholder: PropTypes.string,
  storeData: PropTypes.object.isRequired,
  start: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  currentPage: PropTypes.number.isRequired,
  itemCount: PropTypes.number.isRequired,
  baseUrl: PropTypes.string,
  isOrderSearch: PropTypes.bool.isRequired,
};
